import { useState, ChangeEvent } from 'react';

import { Box, Tabs, Tab, Grid, Typography, useMediaQuery, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoUserTab, { PermissionLabel } from './InfoUserTab';
import SecurityTab from './SecurityTab';
import { ShowBase, ReferenceField, TextField, useRecordContext } from "react-admin";
import ComponentShow, { ShowActions } from '../../../components/PageComponents/ComponentShow';
import DetailHeader from '../../../components/PageComponents/DetailHeader';
import InfoRow from '../../../components/Common/InfoRow';
import QuickViewCard from '../../../components/Common/QuickViewCard';
import { StatusLabel } from '../../../components/Label/status';

const Content = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <ComponentShow
      header={
        <DetailHeader
          kind="User"
          title={record.name}
          status={<StatusLabel statusField="status" />}
          facts={[record.email]}
        />
      }
    >
      <ContentShow />
    </ComponentShow>
  );
};

const UserShow = () => (
  <ShowBase>
    <Content />
  </ShowBase>
);

const QuickView = () => {
  const { t }: { t: any } = useTranslation();
  return (
    <QuickViewCard>
      <InfoRow label={t('Status')}>
        <StatusLabel fontSize="small" statusField="status" />
      </InfoRow>
      <InfoRow label={t('Role')}>
        <PermissionLabel />
      </InfoRow>
      <InfoRow label={t('Account')}>
        <ReferenceField source="accountId" reference="accounts" link="show">
          <TextField source="name" sx={{ fontWeight: 700 }} />
        </ReferenceField>
      </InfoRow>
    </QuickViewCard>
  );
};

const ContentShow = () => {
  const [currentTab, setCurrentTab] = useState<string>('infoUser');
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  const tabs = [
    { value: 'infoUser', label: t('Information') },
    { value: 'security', label: t('Security') }
  ];

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  if (isMobile) {
    return (
      <Box sx={{ px: 2, mt: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ShowActions resource="users" />
        </Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('Information')}
        </Typography>
        <InfoUserTab />
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          {t('Security')}
        </Typography>
        <SecurityTab />
        <Box sx={{ mt: 4 }}>
          <QuickView />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Grid container direction="row" justifyContent="center" spacing={4} sx={{ px: 4 }}>
        <Grid item xs={9} md={9}>
          <Tabs
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 2 }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Grid>
        <Grid item xs={3} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ShowActions resource="users" />
        </Grid>
        <Grid item xs={12} md={8}>
          {currentTab === 'infoUser' && <InfoUserTab />}
          {currentTab === 'security' && <SecurityTab />}
        </Grid>
        <Grid item xs={12} md={4}>
          <QuickView />
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserShow;
